import { Select as UiSelect } from "@rumbledirect/ui";
import { Question, SurveyModel } from "survey-core";
import { createStyles } from "antd-style";
import { Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { QuestionLabel } from "../elements/Question";

/** A single cell in the decision table: a backend value plus a frontend label. */
export interface DecisionTableCell {
  value: string;
  label: string;
}

/** A column definition. `valueKey` is the survey state key the column writes. */
export interface DecisionTableColumn {
  valueKey: string;
  title?: string;
}

/**
 * An external dependency on another survey field. A dependency has no column
 * of its own, but each row must carry a value for it (see `DecisionTable`).
 * `surveyKey` is the survey field whose value rows are matched against; when it
 * changes the whole question is reset.
 */
export interface DecisionTableDependency {
  valueKey: string;
  surveyKey: string;
}

/**
 * The decision table definition stored in the survey JSON. Rows are no longer
 * embedded here; they are fetched from the decision-table API keyed by
 * `decisionTableId`. Each fetched row is aligned to the declared dependencies
 * (in `dependsOn` order) followed by the declared columns (in `columns` order).
 *
 * Note: `dependsOn` lives inside this object (not as a separate question
 * property) on purpose. SurveyJS does not deserialize untyped top-level array
 * properties, but it fully preserves nested arrays inside an object property.
 */
export interface DecisionTable {
  decisionTableId: string;
  columns: DecisionTableColumn[];
  dependsOn?: DecisionTableDependency[];
}

/** Shape of the decision-table API response. */
interface DecisionTableResponse {
  id: string;
  name: string;
  columns: { key: string; label: string | null }[];
  rows: { value: string; label: string | null }[][];
}

export class DecisionTableQuestionModel extends Question {
  getType() {
    return "decisionTable";
  }

  get decisionTable(): DecisionTable | undefined {
    return this.getPropertyValue("decisionTable");
  }
  set decisionTable(val: DecisionTable | undefined) {
    this.setPropertyValue("decisionTable", val);
  }

  /** The UUID of the decision table to fetch from the API. */
  get decisionTableId(): string | undefined {
    return this.decisionTable?.decisionTableId;
  }

  /** External survey-field dependencies (no column, but a value per row). */
  get dependsOn(): DecisionTableDependency[] {
    return this.decisionTable?.dependsOn ?? [];
  }

  /** Ordered, selected backend values, indexed by column. */
  get selectedValues(): (string | null)[] {
    const value = this.value;
    return Array.isArray(value) ? value : [];
  }

  /** The question is only complete when every column has a selected value. */
  isEmpty(): boolean {
    const columns = this.decisionTable?.columns ?? [];
    if (columns.length === 0) return true;
    const selected = this.selectedValues;
    return columns.some(
      (_, index) => selected[index] === undefined || selected[index] === null,
    );
  }
}

interface DecisionTableQuestionProps {
  question: DecisionTableQuestionModel;
}

const useStyles = createStyles(({ css }) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  column: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
  `,
  select: css`
    .ant-select-content input[readonly] {
      border: none !important;
    }
    .ant-select-content-value {
      white-space: normal;
    }
  `,
  selectPopup: css`
    .ant-select-item-option-content {
      white-space: normal;
    }
  `,
}));

/** Distinct cells (by value) at `cellIndex` among the given rows. */
const optionsForCell = (
  rows: DecisionTableCell[][],
  cellIndex: number,
): DecisionTableCell[] => {
  const seen = new Set<string>();
  const options: DecisionTableCell[] = [];
  for (const row of rows) {
    const cell = row[cellIndex];
    if (!cell || seen.has(cell.value)) continue;
    seen.add(cell.value);
    options.push(cell);
  }
  return options;
};

/**
 * Rows still consistent with the selections made in the earlier columns.
 * `offset` is the number of leading dependency cells before the column cells.
 */
const rowsConsistentWith = (
  rows: DecisionTableCell[][],
  selected: (string | null)[],
  upToColumn: number,
  offset: number,
): DecisionTableCell[][] =>
  rows.filter((row) => {
    for (let i = 0; i < upToColumn; i++) {
      if (row[offset + i]?.value !== selected[i]) return false;
    }
    return true;
  });

/**
 * Map an API response into the internal row shape used by the cascade logic.
 *
 * The API orders columns (and each row's cells) by the decision table's own
 * internal column order, which is not necessarily the order we requested keys
 * in. Each row is therefore re-projected onto `orderedKeys`
 * (dependency valueKeys followed by column valueKeys) by looking each key up in
 * the response's `columns`. Rows missing any requested key are skipped.
 */
const responseToRows = (
  response: DecisionTableResponse,
  orderedKeys: string[],
): DecisionTableCell[][] => {
  const indexByKey = new Map<string, number>();
  response.columns.forEach((column, index) =>
    indexByKey.set(column.key, index),
  );

  const cellIndices = orderedKeys.map((key) => indexByKey.get(key));
  if (cellIndices.some((index) => index === undefined)) return [];

  return response.rows
    .map((row): DecisionTableCell[] | null => {
      const cells: DecisionTableCell[] = [];
      for (const index of cellIndices) {
        const cell = row[index as number];
        if (!cell) return null;
        cells.push({ value: cell.value, label: cell.label ?? cell.value });
      }
      return cells;
    })
    .filter((row): row is DecisionTableCell[] => row !== null);
};

export const DecisionTableQuestion = ({
  question,
}: DecisionTableQuestionProps) => {
  const { styles } = useStyles();
  const survey = question.survey as SurveyModel | undefined;

  const columns = question.decisionTable?.columns ?? [];
  const dependsOn = question.dependsOn;
  const selected = question.selectedValues;
  const decisionTableId = question.decisionTableId;

  // Leading cells in every row are the dependency values, in `dependsOn` order.
  const offset = dependsOn.length;

  // The endpoint needs the dependency keys followed by the column keys so that
  // every row carries its dependency values ahead of its selectable columns.
  const keys = [
    ...dependsOn.map((dep) => dep.valueKey),
    ...columns.map((column) => column.valueKey),
  ];

  const rowsQuery = useQuery({
    queryKey: ["decisionTable", decisionTableId, keys],
    queryFn: async () => {
      const response = await survey!.api!.get<DecisionTableResponse>(
        `/decision-tables/${decisionTableId}`,
        { params: { keys } },
      );
      return responseToRows(response.data, keys);
    },
    enabled: !!survey?.api && !!decisionTableId && columns.length > 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const rows = rowsQuery.data ?? [];

  const handleChange = (columnIndex: number, value: string | undefined) => {
    // Keep selections up to (and excluding) the changed column.
    const nextSelected = selected.slice(0, columnIndex);
    if (value !== undefined) {
      nextSelected[columnIndex] = value;
    }
    question.value = nextSelected;

    // Write the changed column's key, then clear every downstream column key
    // because their options (and therefore selections) are no longer valid.
    survey?.setValue(columns[columnIndex].valueKey, value ?? undefined);
    for (let i = columnIndex + 1; i < columns.length; i++) {
      survey?.setValue(columns[i].valueKey, undefined);
    }
  };

  // Reset every selection (question value and the written survey keys).
  const reset = () => {
    question.value = [];
    for (const column of columns) {
      survey?.setValue(column.valueKey, undefined);
    }
  };

  // Only rows whose dependency cells match the current external survey values
  // participate in the cascade.
  const baseRows = rows.filter((row) =>
    dependsOn.every(
      (dep, i) => row[i]?.value === survey?.getValue(dep.surveyKey),
    ),
  );

  // True when the stored selections are no longer reachable given the current
  // dependency values (e.g. the dependency changed on another page while this
  // question was unmounted, so the live listener below could not fire).
  const selectionsAreStale = (): boolean => {
    const setCount = selected.findIndex((v) => v === undefined || v === null);
    const leadingCount = setCount === -1 ? selected.length : setCount;
    if (leadingCount === 0) return false;
    return (
      rowsConsistentWith(baseRows, selected, leadingCount, offset).length === 0
    );
  };

  // When a dependency field changes, the available rows change, so reset.
  const dependencyKey = dependsOn
    .map((dep) => `${dep.surveyKey}=${survey?.getValue(dep.surveyKey) ?? ""}`)
    .join(",");
  useEffect(() => {
    if (!survey || dependsOn.length === 0) return;

    const surveyKeys = new Set(dependsOn.map((dep) => dep.surveyKey));
    const handler = (_sender: SurveyModel, options: { name: string }): void => {
      if (surveyKeys.has(options.name)) reset();
    };
    survey.onValueChanged.add(handler);
    return () => survey.onValueChanged.remove(handler);
  }, [survey, dependencyKey]);

  // Mount-time and dependency-change validation. The live listener above only
  // fires while this question is mounted, so it misses dependency changes made
  // on other pages; re-validate the stored selections here as a safety net.
  //
  // This must wait until the rows have loaded: while the query is in flight
  // `baseRows` is empty, which would make every stored selection look stale and
  // wrongly reset it (e.g. dropping restored selections on page refresh).
  useEffect(() => {
    if (!survey || dependsOn.length === 0) return;
    if (!rowsQuery.isSuccess) return;
    if (selectionsAreStale()) reset();
  }, [survey, dependencyKey, rowsQuery.isSuccess]);

  const isDisabled = question.isDisabledAttr || question.isReadOnly;

  // Compute the currently visible columns (a column is shown once every earlier
  // column has a value) together with their available options.
  const visibleColumns: {
    column: DecisionTableColumn;
    columnIndex: number;
    options: { value: string; label: string }[];
  }[] = [];
  for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
    const previousComplete = columns
      .slice(0, columnIndex)
      .every((_, i) => selected[i] !== undefined && selected[i] !== null);
    if (!previousComplete) break;
    const consistentRows = rowsConsistentWith(
      baseRows,
      selected,
      columnIndex,
      offset,
    );
    const options = optionsForCell(consistentRows, offset + columnIndex).map(
      (cell) => ({ value: cell.value, label: cell.label }),
    );
    visibleColumns.push({ column: columns[columnIndex], columnIndex, options });
  }

  // When a column has exactly one possible option, auto-select it (and persist
  // it via setValue) so the user can't pick anything else and the cascade
  // continues. Handled one column per render; the effect re-runs on change.
  const autoSelectKey = visibleColumns
    .map(
      (v) =>
        `${v.columnIndex}:${v.options.length}:${selected[v.columnIndex] ?? ""}`,
    )
    .join(",");
  useEffect(() => {
    for (const { columnIndex, options } of visibleColumns) {
      if (options.length === 1 && selected[columnIndex] !== options[0].value) {
        handleChange(columnIndex, options[0].value);
        break;
      }
    }
  }, [autoSelectKey]);

  // Render nothing when the rows could not be fetched.
  if (rowsQuery.isError) return null;

  // Show a spinner while the rows are being fetched.
  if (rowsQuery.isLoading) {
    return (
      <div className={styles.container}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {visibleColumns.map(({ column, columnIndex, options }) => {
        // A column with only one possible option is hidden; it is still
        // auto-selected and persisted via the effect above.
        if (options.length === 1) return null;
        return (
          <div className={styles.column} key={column.valueKey}>
            {column.title && (
              <label htmlFor={column.valueKey}>
                <QuestionLabel>{column.title}</QuestionLabel>
              </label>
            )}
            <UiSelect
              id={column.valueKey}
              className={styles.select}
              classNames={{ popup: { root: styles.selectPopup } }}
              style={{ width: "100%" }}
              options={options}
              value={selected[columnIndex] ?? undefined}
              disabled={isDisabled}
              allowClear={!question.isRequired}
              onChange={(value: string | undefined) =>
                handleChange(columnIndex, value)
              }
            />
          </div>
        );
      })}
    </div>
  );
};
